import classNames from 'classnames';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useEffect, useState } from 'react';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import styles from './content-navigation.module.scss';

export interface ContentNavigationProps {
  className?: string;
  currentIndex?: number;
  disableNext?: boolean;
  disablePrevious?: boolean;
  isPlaying?: boolean;
  onClickFullscreen?: () => void;
  onClickNext: () => void;
  onClickPrevious: () => void;
  onTogglePlay?: () => void;
  pageCount?: number;
  showPageCount?: boolean;
}

const ContentNavigation = ({
  className,
  currentIndex,
  disableNext = false,
  disablePrevious = false,
  isPlaying = false,
  pageCount,
  onClickFullscreen,
  onClickNext,
  onClickPrevious,
  onTogglePlay,
  showPageCount = false,
}: ContentNavigationProps) => {
  const { t } = useI18n();
  const [counter, setCounter] = useState('');

  const formatNumber = (value: number) => {
    return value < 10 ? `0${value}` : `${value}`;
  };

  useEffect(() => {
    if (typeof currentIndex !== 'number' || typeof pageCount !== 'number')
      return;

    setCounter(`${formatNumber(currentIndex + 1)}/${formatNumber(pageCount)}`);
  }, [currentIndex, pageCount]);

  return (
    <div
      aria-label={t('items.common.words.navigation')}
      className={classNames(styles.component, className)}
      role="group"
    >
      <Button
        aria-label={t('items.common.words.previousImage')}
        disabled={disablePrevious}
        icon="ChevronLeft"
        onClick={onClickPrevious}
        type="button"
        variant="tertiary"
      />

      {showPageCount ? (
        <Typography
          aria-live="polite"
          className={styles.counter}
          tagStyle="labelMedium"
        >
          {counter}
        </Typography>
      ) : (
        onTogglePlay && (
          <Button
            aria-label={
              isPlaying
                ? t('items.common.words.pauseCarousel')
                : t('items.common.words.playCarousel')
            }
            icon={isPlaying ? 'Pause' : 'Play'}
            onClick={onTogglePlay}
            type="button"
            variant="tertiary"
          />
        )
      )}

      <Button
        aria-label={t('items.common.words.nextImage')}
        disabled={disableNext}
        icon="ChevronRight"
        onClick={onClickNext}
        type="button"
        variant="tertiary"
      />

      {onClickFullscreen && (
        <Button
          aria-label={t('items.common.words.fullScreen')}
          icon="Expand"
          onClick={onClickFullscreen}
          type="button"
          variant="tertiary"
        />
      )}
    </div>
  );
};

ContentNavigation.displayName = 'ContentNavigation';

export default ContentNavigation;
